import { Injectable, inject, Optional, Inject } from '@angular/core';
import { HttpCoreService } from '@dataclouder/ngx-core';
import { TTSRequest, TTSVoice } from '../models/models';
import { TTS_API_URL } from '../tokens';

// Define the AudioGenerated type for the return value of getTextAudioFile
export interface AudioGenerated {
  blob: Blob | null;
  blobUrl: string | null;
  transcription: any | null;
}

// Define the API endpoints
export enum TTSAPI {
  Synthesize = 'api/tts/synthesize',
  ListVoices = 'api/tts/voices',
}

@Injectable({
  providedIn: 'root',
})
export class NgxTtsService {
  private httpService = inject(HttpCoreService);
  private pythonApiUrl: string = 'http://localhost:8000';
  private nodeApiUrl: string = 'http://localhost:8080';

  constructor(@Optional() @Inject(TTS_API_URL) apiUrl: string | null) {
    // If a custom API URL is provided, use it as the primary URL
    if (apiUrl) {
      this.pythonApiUrl = apiUrl;
    }

    // Configure the HttpCoreService with the correct host URLs
    this.httpService.setConfig({
      primaryUrl: this.pythonApiUrl,
      secondaryUrl: this.nodeApiUrl,
      defaultHost: 'primary', // Use Python backend by default
    });
  }

  /**
   * Lists available voices for a specific language code
   * @param languageCode The language code (e.g., 'en', 'es')
   * @returns Promise with the list of available voices
   */
  public async listVoices(languageCode: string): Promise<TTSVoice[]> {
    const response = await this.httpService.get<TTSVoice[]>(`${TTSAPI.ListVoices}?languageCode=${languageCode}`);

    return response;
  }

  /**
   * Generates an audio file from text using the TTS API
   * @param tts The TTS request parameters
   * @returns Promise with the audio data including blob URL and transcription
   */
  public async getTextAudioFile(tts: TTSRequest): Promise<AudioGenerated> {
    // Use the correct endpoint without appending to the base URL
    // The 'secondary' parameter will use the Node backend (http://localhost:8080)
    const httpReq = await this.httpService.postFileAndGetBlob(TTSAPI.Synthesize, tts, 'secondary');
    const audioData: AudioGenerated = { blobUrl: null, transcription: null, blob: null };

    const transcription = httpReq?.headers.get('transcription');

    if (transcription) {
      const data = JSON.parse(transcription);
      audioData.transcription = data;
    }

    audioData.blob = httpReq.body;

    audioData.blobUrl = window.URL.createObjectURL(audioData.blob);

    return audioData;
  }
}
